import { useState, useMemo } from "react";
import {
  Trash2,
  Search,
  Pencil,
  Zap,
  Fuel,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Home as HomeIcon,
  Building2,
  MapPin,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fmtMoney } from "@/lib/currency";
import VehicleChipFilter from "@/components/VehicleChipFilter";

/**
 * Dedicated history table for charging sessions + fuel fill-ups.
 *
 * Adaptive columns — derived from the energy_type mix of the vehicles whose
 * events are currently being shown:
 *   EV-only           → only charge columns (kWh, charge cost, charging notes)
 *   Gas-only          → only fuel columns  (litres/gal, fuel cost, station)
 *   Hybrid / mixed    → union of both; cells render "—" when not applicable
 *
 * Includes search, sortable headers, pagination + rows-per-page.
 */

const TYPE_META = {
  charge: { label: "Charge", icon: Zap, color: "#34D17A" },
  fuel: { label: "Fuel", icon: Fuel, color: "#FFB340" },
};

const LOCATION_META = {
  home: { label: "Home", icon: HomeIcon, color: "#34D17A" },
  work: { label: "Work", icon: Building2, color: "#5AB1FF" },
  other: { label: "Other", icon: MapPin, color: "#8A8A8E" },
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function compareValues(a, b, dir) {
  // null/undefined always sort to the bottom regardless of direction
  const aNil = a == null || a === "";
  const bNil = b == null || b === "";
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  if (typeof a === "number" && typeof b === "number") {
    return dir === "asc" ? a - b : b - a;
  }
  const sa = String(a);
  const sb = String(b);
  return dir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
}

function SortableHeader({ label, sortKey, sortState, onSort, align = "left", className = "" }) {
  const active = sortState.key === sortKey;
  const dir = sortState.dir;
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <TableHead
      className={`label-tiny text-[var(--text-muted)] ${align === "right" ? "text-right" : ""} ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 tap hover:text-[var(--text-primary)] transition-colors ${
          active ? "text-[var(--text-primary)]" : ""
        }`}
        data-testid={`energy-sort-${sortKey}`}
      >
        {label}
        <Icon className="w-3 h-3" strokeWidth={1.75} />
      </button>
    </TableHead>
  );
}

export default function EnergyTable({ events = [], vehicles = [], allVehicles = null, currencySymbol = "$", onDelete, onEdit }) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | charge | fuel
  const [chipVehicleId, setChipVehicleId] = useState("all");
  const [sortState, setSortState] = useState({ key: "date", dir: "desc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Name resolution uses `allVehicles` (includes archived) so any historical
  // event still resolves to a vehicle name. The chip filter uses `vehicles`
  // (active only) so users never see archived chips.
  const veh = useMemo(() => {
    const m = {};
    ((allVehicles || vehicles) || []).forEach((v) => (m[v.id] = v));
    return m;
  }, [allVehicles, vehicles]);

  // Per-active-vehicle event counts — drives chip counters. Pre-search /
  // pre-chip / pre-type-filter so counters stay stable as the user explores.
  const counts = useMemo(() => {
    const c = {};
    vehicles.forEach((v) => (c[v.id] = 0));
    for (const e of events || []) {
      if (c[e.vehicle_id] != null) c[e.vehicle_id] += 1;
    }
    return c;
  }, [vehicles, events]);

  // Decide which columns to render based on the energy_type of vehicles in the
  // active dataset. Always show date + vehicle + type + amount + cost.
  // Charge-only columns: battery deltas. Fuel-only columns: price per L/gal.
  const energyTypesPresent = useMemo(() => {
    const set = new Set();
    (events || []).forEach((e) => {
      const v = veh[e.vehicle_id];
      if (v?.energy_type) set.add(v.energy_type);
    });
    return set;
  }, [events, veh]);

  const showCharge = energyTypesPresent.has("ev") || energyTypesPresent.has("hybrid");
  const showFuel = energyTypesPresent.has("gas") || energyTypesPresent.has("hybrid");

  const filtered = useMemo(() => {
    let out = events || [];
    if (chipVehicleId !== "all") out = out.filter((e) => e.vehicle_id === chipVehicleId);
    if (typeFilter !== "all") out = out.filter((e) => e.type === typeFilter);
    if (q.trim()) {
      const needle = q.toLowerCase();
      out = out.filter(
        (e) =>
          (e.notes || "").toLowerCase().includes(needle) ||
          (veh[e.vehicle_id]?.name || "").toLowerCase().includes(needle) ||
          (e.type || "").toLowerCase().includes(needle) ||
          (e.location_tag || "").toLowerCase().includes(needle) ||
          (e.date || "").includes(needle),
      );
    }
    return out;
  }, [events, q, typeFilter, veh, chipVehicleId]);

  const sorted = useMemo(() => {
    const acc = (e) => {
      switch (sortState.key) {
        case "date":
          return e.date;
        case "vehicle":
          return veh[e.vehicle_id]?.name || "";
        case "type":
          return e.type;
        case "location":
          return e.type === "charge" ? (e.location_tag || "") : "";
        case "amount":
          return e.amount;
        case "cost":
          return e.cost;
        case "ppu":
          return e.price_per_unit;
        case "odometer":
          return e.odometer_km ?? e.end_odometer_km;
        default:
          return null;
      }
    };
    return [...filtered].sort((a, b) => {
      const primary = compareValues(acc(a), acc(b), sortState.dir);
      if (primary !== 0) return primary;
      // Same-date (or otherwise tied) entries: most-recently-logged first.
      const ca = a.created_at || "";
      const cb = b.created_at || "";
      return cb.localeCompare(ca);
    });
  }, [filtered, sortState, veh]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => sorted.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage),
    [sorted, safePage, rowsPerPage],
  );

  const handleSort = (key) => {
    setSortState((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
    setPage(0);
  };

  const colCount = 5 + (showCharge ? 1 : 0) + (showFuel ? 1 : 0) + 1; // date, vehicle, type, amount, cost, [batt], [ppu], notes-or-actions
  // Actual columns: Date, Vehicle, Type, Amount, Cost, [Charge Battery Δ], [Fuel Price/unit], Odometer, Notes, Actions
  const computedColCount = 6 + (showCharge ? 1 : 0) + (showFuel ? 1 : 0) + 2 + 1; // date(1) vehicle(1) type(1) amount(1) cost(1) batt? ppu? odo(1) notes(1) actions(1) → 6+1?+1?+1
  // Simpler: count headers we render
  const headerCount =
    1 /*date*/ +
    1 /*vehicle*/ +
    1 /*type*/ +
    (showCharge ? 1 : 0) /*location*/ +
    1 /*amount*/ +
    1 /*cost*/ +
    (showCharge ? 1 : 0) /*battery delta*/ +
    (showFuel ? 1 : 0) /*price/unit*/ +
    1 /*odometer*/ +
    1 /*notes*/ +
    1; /*actions*/

  return (
    <div
      className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] rounded-lg flex flex-col h-full"
      data-testid="energy-history-table"
    >
      <div className="p-5 pb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium text-[var(--text-primary)]">Energy History</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
            {(showCharge || showFuel) && (
              <span className="text-[var(--text-faint)]">
                {" "}· {showCharge && "charging"}
                {showCharge && showFuel && " + "}
                {showFuel && "fuel"}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] h-9 w-[120px]"
              data-testid="energy-type-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
              <SelectItem value="all">All Types</SelectItem>
              {showCharge && <SelectItem value="charge">Charging</SelectItem>}
              {showFuel && <SelectItem value="fuel">Fuel</SelectItem>}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" strokeWidth={1.5} />
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              placeholder="Search..."
              className="pl-9 w-[180px] sm:w-[220px] bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] h-9 focus-visible:ring-1 focus-visible:ring-[#E82127]/40"
              data-testid="energy-search-input"
            />
          </div>
        </div>
      </div>

      {vehicles.length > 1 && (
        <div className="px-5 pb-3">
          <VehicleChipFilter
            vehicles={vehicles}
            value={chipVehicleId}
            onChange={(next) => {
              setChipVehicleId(next);
              setPage(0);
            }}
            counts={counts}
            totalCount={(events || []).length}
            testIdPrefix="energy"
          />
        </div>
      )}

      <div className="overflow-auto flex-1 max-h-[460px]">
        <Table>
          <TableHeader className="sticky top-0 bg-[var(--bg-elevated)] z-10">
            <TableRow className="border-[var(--border-hover)] hover:bg-transparent">
              <SortableHeader label="Date" sortKey="date" sortState={sortState} onSort={handleSort} />
              <SortableHeader label="Vehicle" sortKey="vehicle" sortState={sortState} onSort={handleSort} />
              <SortableHeader label="Type" sortKey="type" sortState={sortState} onSort={handleSort} />
              {showCharge && (
                <SortableHeader
                  label="Location"
                  sortKey="location"
                  sortState={sortState}
                  onSort={handleSort}
                />
              )}
              <SortableHeader label="Amount" sortKey="amount" sortState={sortState} onSort={handleSort} align="right" />
              <SortableHeader label="Cost" sortKey="cost" sortState={sortState} onSort={handleSort} align="right" />
              {showCharge && (
                <TableHead
                  className="label-tiny text-[var(--text-muted)] text-right hidden md:table-cell"
                  aria-label="Battery delta (start to end percent)"
                >
                  Battery Δ
                </TableHead>
              )}
              {showFuel && (
                <SortableHeader
                  label="Price / unit"
                  sortKey="ppu"
                  sortState={sortState}
                  onSort={handleSort}
                  align="right"
                  className="hidden md:table-cell"
                />
              )}
              <SortableHeader
                label="Odometer"
                sortKey="odometer"
                sortState={sortState}
                onSort={handleSort}
                align="right"
                className="hidden lg:table-cell"
              />
              <TableHead className="label-tiny text-[var(--text-muted)] hidden lg:table-cell">Notes</TableHead>
              <TableHead className="text-right w-[88px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.length === 0 ? (
              <TableRow className="hover:bg-transparent border-[var(--border-hover)]">
                <TableCell colSpan={headerCount} className="text-center py-12 text-[var(--text-muted)]">
                  No energy entries yet. Log a charging session or fuel fill-up.
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((e) => {
                const v = veh[e.vehicle_id];
                const meta = TYPE_META[e.type] || TYPE_META.charge;
                const TypeIcon = meta.icon;
                const isCharge = e.type === "charge";
                const battDelta =
                  isCharge && e.start_battery_pct != null && e.end_battery_pct != null
                    ? `${Math.round(e.start_battery_pct)}→${Math.round(e.end_battery_pct)}%`
                    : null;
                const odo = e.end_odometer_km ?? e.odometer_km;
                const unitLabel = e.unit || (isCharge ? "kWh" : "L");
                return (
                  <TableRow
                    key={e.id}
                    className="border-[var(--border-hover)] hover:bg-[var(--bg-hover)] group"
                    data-testid={`energy-row-${e.id}`}
                  >
                    <TableCell className="font-mono-data text-[var(--text-primary)] text-sm">{e.date}</TableCell>
                    <TableCell className="text-[var(--text-primary)] text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: v?.color || "#8A8A8E" }}
                        />
                        <span className="truncate max-w-[140px]">{v?.name || "Unknown"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase border border-[var(--border-subtle)] text-[var(--text-secondary)]"
                        style={{ borderRadius: 2 }}
                        data-testid={`energy-type-${e.id}`}
                      >
                        <TypeIcon className="w-3 h-3" style={{ color: meta.color }} strokeWidth={2} />
                        {meta.label}
                      </span>
                    </TableCell>
                    {showCharge && (
                      <TableCell>
                        {isCharge ? (() => {
                          const lmeta = LOCATION_META[e.location_tag] || null;
                          if (!lmeta) {
                            return <span className="text-[var(--text-faint)] text-xs">—</span>;
                          }
                          const LIcon = lmeta.icon;
                          return (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)]"
                              data-testid={`energy-location-${e.id}`}
                            >
                              <LIcon className="w-3 h-3" style={{ color: lmeta.color }} strokeWidth={1.75} />
                              {lmeta.label}
                            </span>
                          );
                        })() : (
                          <span className="text-[var(--text-faint)]">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-mono-data text-[var(--text-primary)] text-right">
                      {e.amount != null ? `${(+e.amount).toFixed(2)} ${unitLabel}` : "—"}
                    </TableCell>
                    <TableCell className="font-mono-data text-[var(--text-primary)] text-right">
                      {e.cost ? fmtMoney(e.cost, currencySymbol, 2) : <span className="text-[var(--text-faint)]">—</span>}
                    </TableCell>
                    {showCharge && (
                      <TableCell className="font-mono-data text-right hidden md:table-cell">
                        {isCharge && battDelta ? (
                          <span className="text-[var(--text-primary)] text-xs">{battDelta}</span>
                        ) : (
                          <span className="text-[var(--text-faint)]">—</span>
                        )}
                      </TableCell>
                    )}
                    {showFuel && (
                      <TableCell className="font-mono-data text-right hidden md:table-cell">
                        {e.price_per_unit ? (
                          <span className="text-[var(--text-primary)] text-xs">
                            {fmtMoney(e.price_per_unit, currencySymbol, 3)}
                            <span className="text-[var(--text-faint)]">/{e.unit || "L"}</span>
                          </span>
                        ) : (
                          <span className="text-[var(--text-faint)]">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="font-mono-data text-right hidden lg:table-cell">
                      {odo != null ? (
                        <span className="text-[var(--text-primary)] text-xs">{Math.round(odo).toLocaleString()}</span>
                      ) : (
                        <span className="text-[var(--text-faint)]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#a8a8ae] text-sm max-w-[200px] truncate hidden lg:table-cell">
                      {e.notes || <span className="text-[var(--text-faint)]">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-transparent"
                            onClick={() => onEdit(e)}
                            data-testid={`energy-edit-${e.id}`}
                            aria-label="Edit entry"
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-[var(--text-muted)] hover:text-[#FF3B30] hover:bg-transparent"
                          onClick={() => onDelete(e.id)}
                          data-testid={`energy-delete-${e.id}`}
                          aria-label="Delete entry"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="px-5 py-3 border-t border-[var(--border-hover)] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
          <span>Rows per page</span>
          <Select
            value={String(rowsPerPage)}
            onValueChange={(v) => {
              setRowsPerPage(parseInt(v, 10));
              setPage(0);
            }}
          >
            <SelectTrigger
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] h-7 w-[72px]"
              data-testid="energy-rows-per-page"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
              {ROWS_PER_PAGE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
          <span data-testid="energy-page-indicator">
            Page <span className="text-[var(--text-primary)]">{safePage + 1}</span> of {totalPages} ·{" "}
            <span className="text-[var(--text-primary)]">{sorted.length}</span> total
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)] disabled:opacity-40"
              disabled={safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="energy-prev-page"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)] disabled:opacity-40"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              data-testid="energy-next-page"
              aria-label="Next page"
            >
              <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
